import {
  CheckCircle,
  Error as ErrorIcon,
  RadioButtonChecked,
  RadioButtonUnchecked,
  RemoveCircleOutline,
} from "@mui/icons-material";
import { Stack, Typography, type SxProps, type Theme } from "@mui/material";
import type React from "react";
import {
  IngestionTestRunMode,
  IngestionTestRunState,
} from "../../generated/graphql";

const styles = {
  step: { minHeight: 32 },
  reached: { color: "success.main" },
  current: { color: "info.main" },
  failed: { color: "error.main" },
  indeterminate: { color: "text.secondary" },
  pending: { color: "text.disabled" },
  timestamp: { fontFamily: "monospace" },
} satisfies Record<string, SxProps<Theme>>;

/**
 * How a single timeline step is drawn. `reached`/`current`/`pending` are the
 * ordinary progression; `failed` is the error terminal; `indeterminate` is the
 * pre-cleanup terminal of a CLEANED run, whose original outcome (COMPLETED or
 * FAILED) the DB no longer records.
 */
type StepStatus =
  | "reached"
  | "current"
  | "pending"
  | "failed"
  | "indeterminate";

type TimelineStep = {
  /** Stable React key. */
  readonly key: string;
  /** The label to render. */
  readonly label: string;
  readonly status: StepStatus;
  /** The real timestamp behind the step, or `null` when none is known. */
  readonly timestamp: string | null;
};

const label = (state: IngestionTestRunState): string => state;

/**
 * Build the ordered steps for a run. The row carries only two real timestamps —
 * `createdAt` (the UPLOADED birth) and `updatedAt` (the latest transition) — so
 * those anchor the first step and the terminal/current step; the DB persists no
 * per-state timestamp, so intermediate steps are left un-timed rather than
 * showing a fabricated time.
 *
 * A CLEANED run is special: its pre-cleanup terminal is rendered indeterminate,
 * never a green COMPLETED, because the row no longer records which terminal it
 * passed through before cleanup. What CLEANED means depends on the mode: a
 * PREVIEW_E2E purge deletes the run's artifacts, while a FULL_APPLY cleanup
 * only purges outbox metadata — its authoritative order, scanned order, and
 * document are retained — so the labels spell out which happened.
 */
const stepsFor = (
  state: IngestionTestRunState,
  mode: IngestionTestRunMode,
  createdAt: string,
  updatedAt: string,
): readonly TimelineStep[] => {
  const lead: readonly IngestionTestRunState[] = [
    IngestionTestRunState.Uploaded,
    IngestionTestRunState.Dispatched,
    IngestionTestRunState.Extracting,
  ];
  const leadOrder = [
    IngestionTestRunState.Uploaded,
    IngestionTestRunState.Dispatched,
    IngestionTestRunState.Extracting,
    IngestionTestRunState.Completed,
  ];
  const currentIndex = leadOrder.indexOf(state);

  const leadSteps: readonly TimelineStep[] = lead.map((s, index) => {
    const isCurrent = s === state;
    return {
      key: s,
      label: label(s),
      status: isCurrent
        ? "current"
        : index < currentIndex
          ? "reached"
          : "pending",
      timestamp: index === 0 ? createdAt : isCurrent ? updatedAt : null,
    };
  });

  if (state === IngestionTestRunState.Cleaned) {
    const isFullApply = mode === IngestionTestRunMode.FullApply;
    return [
      ...leadSteps,
      {
        key: "pre-cleanup-terminal",
        label: isFullApply
          ? "COMPLETED or FAILED"
          : "COMPLETED or FAILED — purged",
        status: "indeterminate",
        timestamp: null,
      },
      {
        key: IngestionTestRunState.Cleaned,
        label: isFullApply
          ? "CLEANED — outbox metadata purged; order, scanned order, and document retained"
          : label(IngestionTestRunState.Cleaned),
        status: "current",
        timestamp: updatedAt,
      },
    ];
  }

  const terminal: TimelineStep =
    state === IngestionTestRunState.Failed
      ? {
          key: IngestionTestRunState.Failed,
          label: label(IngestionTestRunState.Failed),
          status: "failed",
          timestamp: updatedAt,
        }
      : {
          key: IngestionTestRunState.Completed,
          label: label(IngestionTestRunState.Completed),
          status:
            state === IngestionTestRunState.Completed ? "current" : "pending",
          timestamp:
            state === IngestionTestRunState.Completed ? updatedAt : null,
        };

  return [...leadSteps, terminal];
};

const iconFor = (status: StepStatus): React.ReactNode => {
  if (status === "failed")
    return <ErrorIcon fontSize="small" sx={styles.failed} />;
  if (status === "indeterminate")
    return <RemoveCircleOutline fontSize="small" sx={styles.indeterminate} />;
  if (status === "reached")
    return <CheckCircle fontSize="small" sx={styles.reached} />;
  if (status === "current")
    return <RadioButtonChecked fontSize="small" sx={styles.current} />;
  return <RadioButtonUnchecked fontSize="small" sx={styles.pending} />;
};

const labelSx = (status: StepStatus): SxProps<Theme> | undefined => {
  if (status === "failed") return styles.failed;
  if (status === "current") return styles.current;
  if (status === "indeterminate") return styles.indeterminate;
  if (status === "pending") return styles.pending;
  return undefined;
};

/**
 * Renders the run's lifecycle as a vertical timeline, marking each state
 * reached / current / pending, with the error and post-cleanup terminals handled
 * distinctly.
 *
 * @param props.state The run's current lifecycle state.
 * @param props.mode The run's mode — routes what CLEANED is rendered to mean.
 * @param props.createdAt The run row's creation time (UPLOADED).
 * @param props.updatedAt The run row's last-transition time (current state).
 */
const IngestionTestRunTimeline = ({
  state,
  mode,
  createdAt,
  updatedAt,
}: {
  readonly state: IngestionTestRunState;
  readonly mode: IngestionTestRunMode;
  readonly createdAt: string;
  readonly updatedAt: string;
}) => {
  const steps = stepsFor(state, mode, createdAt, updatedAt);

  return (
    <Stack spacing={0.5}>
      {steps.map((step) => (
        <Stack
          key={step.key}
          direction="row"
          spacing={1}
          alignItems="center"
          sx={styles.step}
        >
          {iconFor(step.status)}
          <Typography variant="body2" sx={labelSx(step.status)}>
            {step.label}
          </Typography>
          {step.timestamp !== null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={styles.timestamp}
            >
              {new Date(step.timestamp).toLocaleString()}
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export default IngestionTestRunTimeline;
